import type { User } from "@/pages/Public/pages/Login/schema/login";

import type { DraftSender } from "./inboxDraft.types";

export interface SenderContext {
	signature?: string;
	senderFirstName?: string;
	senderLastName?: string;
}

/**
 * Resolves the actual sender data ({signature}, {senderFirstName},
 * {senderLastName}) for a draft's selected `from` sender, so the chat preview
 * can show the data that will actually be sent.
 *
 * Email drafts resolve the signature + sender name from the matching email
 * account (by `emailId`, the canonical join used across the campaign builder);
 * LinkedIn drafts have no signature, so only the sender's name is returned.
 */
export const resolveSenderContext = (
	from: DraftSender | undefined,
	users: User[]
): SenderContext => {
	if (!from?.userId) {
		return {};
	}

	const user = users.find((u) => u.id === from.userId);

	if (!user) {
		return {};
	}

	if (from.type === "email") {
		const email = user.emails?.find((e) => e.id === from.emailId);

		return {
			signature: email?.signature ?? undefined,
			senderFirstName: email?.senderFirstName || user.firstName,
			senderLastName: email?.senderLastName || user.lastName,
		};
	}

	// LinkedIn (or any non-email) drafts: no signature, names from the user.
	return {
		senderFirstName: user.firstName,
		senderLastName: user.lastName,
	};
};
